import React, { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AccountBalanceWallet,
  AdminPanelSettings,
  CheckCircle,
  HourglassEmpty,
  Refresh,
  Search,
  ErrorOutline,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fetchAdminCollections, fetchAdminCollectionsSummary } from '../hooks/hooks';

const PAGE_SIZE = 50;

const STATUS_CHIP_COLOR = {
  completed: 'success',
  pending: 'warning',
  failed: 'error',
};

const formatNum = (val) => Number(val || 0).toLocaleString();
const formatMoney = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const StatCard = ({ icon: Icon, label, value, color, cardBg, borderClr }) => (
  <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px', height: '100%' }}>
    <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
      <Stack direction="row" alignItems="center" gap={1} mb={0.75}>
        <Icon sx={{ fontSize: 18, color: color || '#0BA480' }} />
        <Typography variant="body2" color="text.secondary" noWrap>
          {label}
        </Typography>
      </Stack>
      <Typography variant="h5" fontWeight={700}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const AdminCollections = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#fff';
  const borderClr = isDark ? 'rgba(255,255,255,0.09)' : '#E2E8F0';

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary,
  } = useQuery({
    queryKey: ['adminCollectionsSummary'],
    queryFn: fetchAdminCollectionsSummary,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['adminCollections', page, statusFilter, search],
    queryFn: () => fetchAdminCollections({
      page: page + 1,
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(search ? { search } : {}),
    }),
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });

  const rows = Array.isArray(data?.results) ? data.results : [];
  const count = data?.count || 0;

  const statCards = summaryLoading ? [] : [
    { icon: AccountBalanceWallet, label: 'Total Collected', value: formatMoney(summary?.total_collected) },
    { icon: CheckCircle, label: 'Completed', value: formatNum(summary?.completed_count), color: '#0BA480' },
    { icon: HourglassEmpty, label: 'Pending', value: formatNum(summary?.pending_count), color: '#F59E0B' },
    { icon: ErrorOutline, label: 'Failed', value: formatNum(summary?.failed_count), color: '#EF4444' },
  ];

  const handleRefreshAll = () => {
    refetchSummary();
    refetch();
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
            <AdminPanelSettings sx={{ color: '#0BA480', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>
              Collections
            </Typography>
            <Chip label="Admin" size="small" color="success" sx={{ fontWeight: 600 }} />
          </Stack>
          <Typography color="text.secondary" fontSize={14}>
            Wallet cash-in deposits across all users.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefreshAll}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Stack>

      {(summaryError || error) && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
          Failed to load some collections data. Please try refreshing.
        </Alert>
      )}

      {summaryLoading ? (
        <Typography color="text.secondary" sx={{ mb: 3 }}>Loading summary...</Typography>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {statCards.map((s) => (
            <Grid item xs={6} sm={3} key={s.label}>
              <StatCard {...s} cardBg={cardBg} borderClr={borderClr} />
            </Grid>
          ))}
        </Grid>
      )}

      <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px' }}>
        <Box sx={{ px: 2, pt: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" gap={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>
              All Collections {isLoading ? '' : `(${formatNum(count)})`}
            </Typography>
            <Stack direction="row" gap={1.5}>
              <TextField
                size="small"
                select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </TextField>
              <TextField
                size="small"
                placeholder="Search user, msisdn, ref..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                InputProps={{ startAdornment: <Search sx={{ fontSize: 18, mr: 0.75, color: 'text.disabled' }} /> }}
                sx={{ minWidth: 240 }}
              />
            </Stack>
          </Stack>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Account ID</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }} align="right">Amount</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }} align="right">Fee</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Vendor</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>MSISDN</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Transaction ID</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">Loading collections...</Typography>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">No collections found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} sx={{ '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'action.hover' } }}>
                    <TableCell>
                      <Typography fontSize={12.5}>
                        {r.created_at ? format(new Date(r.created_at), 'MMM d, yyyy HH:mm') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={13} fontWeight={500}>{r.username || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontFamily="monospace" fontSize={12.5}>{r.account_id || '—'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontSize={13} fontWeight={600}>{formatMoney(r.amount)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontSize={13}>{formatMoney(r.fee)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={r.status}
                        size="small"
                        color={STATUS_CHIP_COLOR[r.status] || 'default'}
                        sx={{ fontSize: 11, textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={13}>{r.vendor || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={13}>{r.msisdn || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontFamily="monospace" fontSize={12}>{r.transid || '—'}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {count > 0 && (
          <TablePagination
            component="div"
            count={count}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
          />
        )}
      </Card>
    </Container>
  );
};

export default AdminCollections;
